/**
 * Deals API service for managing sales deals.
 * @module lib/api/deals.api
 */

import {
  Deal,
  CreateDeal,
  UpdateDeal,
  ConvertDealRequest,
  ConvertDealResult,
  DealForecastRollup,
  DealPipelineMovement,
  DealProbabilityHistoryEntry,
  PaginatedResponse,
  PaginationParams,
  ImportPreviewRecord,
  ImportOptions,
  ImportResult,
  ExportOptions,
  createPaginationMeta
} from '@scrambl/shared';
import { BaseApi } from './base-api';
import { useOrganizationStore } from '@/store/organization.store';
import { createSingletonApi } from './singleton-factory';
class DealApiService extends BaseApi<Deal, CreateDeal, UpdateDeal> {
  constructor() {
    super({
      baseEndpoint: '/deals',
      responseNormalization: true
    });
}

  // Override create to add organization ID
  async create(data: CreateDeal): Promise<Deal> {
    const { currentOrganization } = useOrganizationStore.getState();
    
    const dealData = this.addToOrganizationData(data, currentOrganization?.id);
    
    // Add backend-specific fields that aren't in the CreateDeal type
    const dealDataWithBackendFields = {
      ...dealData,
      deletedAt: null, // Backend requirement
    } as CreateDeal;
    
    return super.create(dealDataWithBackendFields);
  }

  // Override the list method to handle deals-specific caching
  async list(params: Partial<PaginationParams> = {}): Promise<PaginatedResponse<Deal>> {
    const result = await super.list(params);
    return result;
  }

  // Custom method for deals-specific functionality
  async getDealsForOrganization(organizationId: string): Promise<Deal[]> {
    const response = await this.makeRequest<PaginatedResponse<Deal>>(`/deals?organizationId=${organizationId}`);
    return response.data;
  }

  // Alias methods for backward compatibility
  async getDeals(params: Partial<PaginationParams & { name?: string; stage?: string; stageTypeId?: string; primaryContactId?: string; contactId?: string; closeDateFrom?: string; closeDateTo?: string; recordState?: string; divergenceOnly?: boolean; columnFilters?: Record<string, unknown> }> = {}): Promise<PaginatedResponse<Deal>> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params.name) searchParams.set('name', params.name);
    if (params.stage) searchParams.set('stage', params.stage);
    if (params.stageTypeId) searchParams.set('stageTypeId', params.stageTypeId);
    if (params.primaryContactId) searchParams.set('primaryContactId', params.primaryContactId);
    if (params.contactId) searchParams.set('contactId', params.contactId);
    if (params.closeDateFrom) searchParams.set('closeDateFrom', params.closeDateFrom);
    if (params.closeDateTo) searchParams.set('closeDateTo', params.closeDateTo);
    // T2 record-state opt-in: 'all' or an explicit state; unset = server
    // default (active + provisional, tombstones hidden).
    if (params.recordState) searchParams.set('recordState', params.recordState);
    // C13/T7: backend reads the string 'true' → |rep − derived| >= threshold.
    if (params.divergenceOnly) searchParams.set('divergenceOnly', 'true');
    if (params.columnFilters && Object.keys(params.columnFilters).length > 0) {
      searchParams.set('columnFilters', JSON.stringify(params.columnFilters));
    }

    const queryString = searchParams.toString();
    return this.makeRequest<PaginatedResponse<Deal>>(`${this.config.baseEndpoint}?${queryString}`, {
      method: 'GET',
    });
  }

  async getDeal(id: string): Promise<Deal> {
    return this.findById(id);
  }

  async createDeal(data: CreateDeal): Promise<Deal> {
    return this.create(data);
  }

  async updateDeal(id: string, data: UpdateDeal): Promise<Deal> {
    return this.update(id, data);
  }

  async deleteDeal(id: string): Promise<void> {
    return this.delete(id);
  }

  async bulkDeleteDeals(ids: string[]): Promise<{ deletedCount: number }> {
    return this.makeRequest<{ deletedCount: number }>(`${this.config.baseEndpoint}/bulk-delete`, {
      method: 'POST',
      body: { ids }
    });
  }

  /**
   * Won-deal conversion: creates a DRAFT contract (+deliverables, optional
   * project link) from a WON deal. 409 = already converted (response carries
   * the existing contractId).
   */
  async convertDeal(id: string, payload: ConvertDealRequest): Promise<ConvertDealResult> {
    return this.makeRequest<ConvertDealResult>(
      `${this.config.baseEndpoint}/${id}/convert`,
      { method: 'POST', body: payload },
    );
  }

  // T8 — append-only rep-confidence change log for a deal, newest first.
  async getProbabilityHistory(
    id: string,
    limit?: number,
  ): Promise<DealProbabilityHistoryEntry[]> {
    const qs = limit ? `?limit=${limit}` : '';
    return this.makeRequest<DealProbabilityHistoryEntry[]>(
      `${this.config.baseEndpoint}/${id}/probability-history${qs}`,
      { method: 'GET' },
    );
  }

  async recalculateScores(): Promise<{ updated: number }> {
    return this.makeRequest<{ updated: number }>(
      `${this.config.baseEndpoint}/recalculate-scores`,
      {
        method: 'POST',
      },
    );
  }

  /** Period forecast roll-up: category totals, called vs derived-weighted, divergences. */
  async getForecast(periodStart: string, periodEnd: string): Promise<DealForecastRollup> {
    return this.makeRequest<DealForecastRollup>(
      `${this.config.baseEndpoint}/forecast?periodStart=${periodStart}&periodEnd=${periodEnd}`,
      { method: 'GET' },
    );
  }

  /** What changed since a baseline date (snapshot diff). */
  async getPipelineMovement(since: string): Promise<DealPipelineMovement> {
    return this.makeRequest<DealPipelineMovement>(
      `${this.config.baseEndpoint}/pipeline-movement?since=${since}`,
      { method: 'GET' },
    );
  }

  /** 1.5 risk gauge: aggregate win rate + median time-to-close over a trailing window. */
  async getWinRateStats(
    from: string,
    to: string,
  ): Promise<{
    from: string;
    to: string;
    wonCount: number;
    lostCount: number;
    closedCount: number;
    winRate: number | null;
    medianTimeToCloseDays: number | null;
  }> {
    return this.makeRequest(
      `${this.config.baseEndpoint}/win-rate-stats?from=${from}&to=${to}`,
      { method: 'GET' },
    );
  }

  // Compatibility wrapper for old lead callers. Lead ids resolve to the
  // migrated contact id, and deals are read by primary/contact relationship.
  async getDealsByLeadId(leadId: string, filters?: { limit?: number }): Promise<PaginatedResponse<Deal>> {
    const limit = filters?.limit ?? 500;
    const resolved = await this.makeRequest<{ contactId: string | null }>(`/contacts/resolve-lead/${leadId}`, {
      method: 'GET',
    });

    if (!resolved.contactId) {
      return { data: [], meta: createPaginationMeta(1, limit, 0) };
    }

    return this.getDeals({
      contactId: resolved.contactId,
      limit,
    });
  }

  // Import/Export operations
  previewImport(records: unknown[], options: ImportOptions): Promise<ImportPreviewRecord[]> {
    return this.makeRequest<ImportPreviewRecord[]>(`${this.config.baseEndpoint}/import/preview`, {
      method: 'POST',
      body: { records, options }
    });
  }

  import(data: { records: ImportPreviewRecord[]; options: ImportOptions }): Promise<ImportResult> {
    return this.makeRequest<ImportResult>(`${this.config.baseEndpoint}/import`, {
      method: 'POST',
      body: data
    });
  }

  export(options: ExportOptions): Promise<Deal[]> {
    return this.makeRequest<Deal[]>(`${this.config.baseEndpoint}/export`, {
      method: 'POST',
      body: options
    });
  }
}

// Export singleton instance using factory
export const dealApiService = createSingletonApi(DealApiService);

// Export for backward compatibility
export const dealApi = dealApiService;

// Legacy export
export { dealApiService as DealApi };
